import { useMemo } from 'react';
import { useAppSelector } from 'app/hooks';
import { selectRelationshipLabelByCode } from 'features/masters/mastersSelectors';

/**
 * Enriquece una lista de relaciones/usuarios relacionados sustituyendo
 * el código crudo de `relationship.code` por su etiqueta traducida
 * (obtenida del catálogo RELATIONSHIPS cargado con locale en el store).
 *
 * El campo `relation` pre-computado por los normalizadores contiene solo
 * el código (o cadena vacía). Este hook lo re-mapea usando el selector
 * vivo de Redux, resolviendo races entre la carga del listado y la
 * carga del catálogo de relationships.
 *
 * @param dataList - Lista normalizada cuyos items pueden tener `relationship.code`.
 * @returns Nueva lista con `relation` traducido (fallback: code → valor previo).
 *
 * @example
 * const dataList = useAppSelector((s) => selectRelations(s, contextKey));
 * const enrichedDataList = useRelationshipEnriched(dataList);
 * // <TableComponent dataList={enrichedDataList} ... />
 */
export const useRelationshipEnriched = (dataList: any[] | null | undefined): any[] => {
  const relationshipLabelByCode = useAppSelector(selectRelationshipLabelByCode);

  return useMemo(
    () =>
      (dataList ?? []).map((item: any) => ({
        ...item,
        relation: item.relationship?.code
          ? (relationshipLabelByCode[item.relationship.code] ?? item.relationship.code)
          : item.relation,
      })),
    [dataList, relationshipLabelByCode]
  );
};
